import type { InferenceSession, Tensor as OrtTensor } from 'onnxruntime-web';

const MODEL_URL = 'https://huggingface.co/tomjackson2023/rembg/resolve/main/u2netp.onnx';
const SIZE = 320;
const MEAN = [0.485, 0.456, 0.406];
const STD = [0.229, 0.224, 0.225];

let sessionPromise: Promise<InferenceSession> | null = null;

const loadSession = async (): Promise<InferenceSession> => {
  if (!sessionPromise) {
    sessionPromise = (async () => {
      const ort = await import('onnxruntime-web');
      ort.env.wasm.wasmPaths = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ort.env.versions.web || '1.27.0'}/dist/`;
      const res = await fetch(MODEL_URL);
      if (!res.ok) throw new Error('Não foi possível baixar o modelo de recorte');
      const buffer = new Uint8Array(await res.arrayBuffer());
      return ort.InferenceSession.create(buffer, { executionProviders: ['wasm'] });
    })().catch((e) => {
      sessionPromise = null;
      throw e;
    });
  }
  return sessionPromise;
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Falha ao carregar imagem'));
    img.src = src;
  });

export const removeBackground = async (src: string): Promise<string> => {
  const ort = await import('onnxruntime-web');
  const session = await loadSession();
  const img = await loadImage(src);

  const small = document.createElement('canvas');
  small.width = SIZE;
  small.height = SIZE;
  const sctx = small.getContext('2d');
  if (!sctx) throw new Error('Canvas indisponível');
  sctx.drawImage(img, 0, 0, SIZE, SIZE);
  const { data } = sctx.getImageData(0, 0, SIZE, SIZE);

  const input = new Float32Array(SIZE * SIZE * 3);
  for (let i = 0; i < SIZE * SIZE; i++) {
    const r = data[i * 4] / 255;
    const g = data[i * 4 + 1] / 255;
    const b = data[i * 4 + 2] / 255;
    input[i] = (r - MEAN[0]) / STD[0];
    input[SIZE * SIZE + i] = (g - MEAN[1]) / STD[1];
    input[SIZE * SIZE * 2 + i] = (b - MEAN[2]) / STD[2];
  }

  const tensor: OrtTensor = new ort.Tensor('float32', input, [1, 3, SIZE, SIZE]);
  const feeds: Record<string, OrtTensor> = { [session.inputNames[0]]: tensor };
  const output = await session.run(feeds);
  const mask = output[session.outputNames[0]].data as Float32Array;

  let mi = Infinity;
  let ma = -Infinity;
  for (let i = 0; i < mask.length; i++) {
    if (mask[i] < mi) mi = mask[i];
    if (mask[i] > ma) ma = mask[i];
  }
  const range = ma - mi || 1;

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = SIZE;
  maskCanvas.height = SIZE;
  const mctx = maskCanvas.getContext('2d');
  if (!mctx) throw new Error('Canvas indisponível');
  const maskImg = mctx.createImageData(SIZE, SIZE);
  for (let i = 0; i < SIZE * SIZE; i++) {
    const a = Math.round(((mask[i] - mi) / range) * 255);
    maskImg.data[i * 4] = a;
    maskImg.data[i * 4 + 1] = a;
    maskImg.data[i * 4 + 2] = a;
    maskImg.data[i * 4 + 3] = 255;
  }
  mctx.putImageData(maskImg, 0, 0);

  const out = document.createElement('canvas');
  out.width = img.naturalWidth;
  out.height = img.naturalHeight;
  const octx = out.getContext('2d');
  if (!octx) throw new Error('Canvas indisponível');
  octx.drawImage(img, 0, 0);
  const base = octx.getImageData(0, 0, out.width, out.height);

  const upscale = document.createElement('canvas');
  upscale.width = out.width;
  upscale.height = out.height;
  const uctx = upscale.getContext('2d');
  if (!uctx) throw new Error('Canvas indisponível');
  uctx.drawImage(maskCanvas, 0, 0, out.width, out.height);
  const upMask = uctx.getImageData(0, 0, out.width, out.height).data;

  for (let i = 0; i < out.width * out.height; i++) {
    base.data[i * 4 + 3] = upMask[i * 4];
  }
  octx.putImageData(base, 0, 0);

  return out.toDataURL('image/png');
};
